<template>
  <div ref="autocompleteWrapper" class="position-relative">
    <slot
      name="input"
      v-bind="{
        search,
        showList,
        keepOpen
      }"
    >
      <div :class="[{ 'form-floating': floatingLabel }]">
        <label v-if="label && !floatingLabel" :for="inputId" class="glass-label">
          {{ label }}<span v-if="required" class="text-danger ms-1">*</span>
        </label>
        <loading-overlay :loading="loadingInput">
          <div class="input-with-icon-wrapper position-relative">
            <!-- Icône de la catégorie sélectionnée ou icône par défaut -->
            <div
              v-if="!showList"
              class="selected-category-icon"
              :class="{ 'default-icon': !selectedItem || !selectedItem.icone }"
              :style="
                selectedItem && selectedItem.icone
                  ? {
                      '--selected-color': hexToRgb(selectedItem.couleur || '#6ab4f8')
                    }
                  : {}
              "
            >
              <font-awesome-icon
                v-if="selectedItem && selectedItem.icone"
                :icon="'fa-solid fa-' + selectedItem.icone"
              />
              <font-awesome-icon v-else icon="fa-solid fa-tag" />
            </div>

            <input
              :id="inputId"
              v-model="search"
              role="combobox"
              :aria-expanded="showList || keepOpen"
              :aria-autocomplete="'list'"
              :aria-controls="`${inputId}-listbox`"
              :aria-activedescendant="selectedId ? `option-${selectedId}` : undefined"
              :required="required"
              :aria-required="required"
              class="glass-input"
              :class="[
                showList ? 'rounded-bottom-0' : '',
                { 'floating-input': floatingLabel },
                { 'input-with-icon': !showList }
              ]"
              :placeholder="placeholder"
              :disabled="disabled"
              autocomplete="off"
              type="search"
              @focus="onFocus"
              @blur="blurred"
            />
          </div>
        </loading-overlay>
        <label v-if="label && floatingLabel" :for="inputId" class="glass-label">
          {{ label }}<span v-if="required" class="text-danger ms-1">*</span>
        </label>
      </div>
      <div
        v-if="multiple && selectedIds.length && !hideChoices"
        class="d-flex flex-wrap gap-1 mt-1 justify-content-start"
        role="list"
        aria-label="Éléments sélectionnés"
      >
        <span
          v-for="id in selectedIds"
          :key="id"
          class="badge bg-primary d-flex align-items-center"
          role="listitem"
        >
          {{
            getInputLibelleFromId({
              id,
              items,
              getInputLibelle
            })
          }}
          <button
            type="button"
            class="btn-close btn-close-white ms-2"
            :aria-label="`Supprimer ${getInputLibelleFromId({ id, items, getInputLibelle })}`"
            @click="deleteItem(id)"
          ></button>
        </span>
      </div>
    </slot>

    <Teleport to="body">
      <div
        v-if="showList || keepOpen"
        :id="`${inputId}-listbox`"
        role="listbox"
        :aria-label="label || 'Options disponibles'"
        class="autocomplete-dropdown border border-1 bg-white position-absolute"
        :style="dropdownStyle"
        style="z-index: 1070"
        @mousedown="keepOpen = true"
      >
        <loading-overlay :loading="loading">
          <div
            v-if="listItems && listItems.length > 0"
            :style="{
              maxHeight: maxResultsHeight,
              overflow: 'auto'
            }"
          >
            <div
              v-for="item in listItems"
              :key="getId(item)"
              :id="`option-${getId(item)}`"
              role="option"
              :aria-selected="selectedId === getId(item)"
              tabindex="0"
              class="option p-2"
              :class="{ 'option-selected': selectedId === getId(item) }"
              @click="selectItem(getId(item))"
            >
              <slot
                v-if="getId(item) === null || getId(item) === undefined"
                name="empty-option"
                v-bind="item"
              >
                <span class="fst-italic">Aucune sélection</span>
              </slot>
              <slot v-else name="options" v-bind="item">
                <div class="d-flex align-items-center">
                  <div
                    v-if="item.icone"
                    class="option-icon-box me-2"
                    :style="{
                      '--option-color': hexToRgb(item.couleur || '#6ab4f8')
                    }"
                  >
                    <font-awesome-icon :icon="'fa-solid fa-' + item.icone" />
                  </div>
                  <span class="text-wrap">{{ getInputLibelle(item) }}</span>
                </div>
              </slot>
            </div>
          </div>
          <template v-else>
            <div
              class="p-2 border border-1 border-top-0 border-end-0 border-start-0"
              @click="
                () => {
                  showList = false
                  keepOpen = false
                }
              "
            >
              <slot name="no-options">Aucun résultat</slot>
            </div>
          </template>
          <slot
            name="additional-option"
            v-bind="{
              items,
              search
            }"
          ></slot>
        </loading-overlay>
      </div>
    </Teleport>
  </div>
</template>

<script>
import LoadingOverlay from '@/components/UI/LoadingOverlay.vue'

const normalize = function (chaine, lower = true) {
  if (!chaine) return null
  let res = chaine
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

  if (lower) return res.toLowerCase()

  return res
}

const defaultFilteringFunction = ({ search, items }) => {
  if (!search) return items

  const normalizedSearch = normalize(search)

  // filter items based on the search term recursively calling himself if is an object
  return items.filter((item) => {
    if (typeof item === 'object') {
      return Object.values(item).some((value) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return normalize(value.toString()).includes(normalizedSearch)
        } else if (Array.isArray(value)) {
          return value.some((val) => normalize(val.toString()).includes(normalizedSearch))
        } else if (value && typeof value === 'object') {
          return defaultFilteringFunction({ search: normalizedSearch, items: [value] }).length > 0
        }
        return false
      })
    }
    return normalize(item.toString()).includes(normalizedSearch)
  })
}

const defaultGetInputLibelle = (item) => {
  return item?.libelle ?? item?.label ?? item?.name ?? 'Aucune valeur'
}

const defaultGetInputLibelleFromId = ({ id, items, getInputLibelle }) => {
  if (!items) return null

  const item = items.find((item) => item?.id === id)
  return getInputLibelle(item)
}

export default {
  name: 'AutocompleteComponent',
  components: { LoadingOverlay },
  props: {
    items: Array,
    required: Boolean,
    getInputLibelle: {
      type: Function,
      default: (item) => {
        return defaultGetInputLibelle(item)
      }
    },

    getInputLibelleFromId: {
      type: Function,
      default: ({ id, items, getInputLibelle }) => {
        if (!items) items = null

        if (!getInputLibelle) getInputLibelle = defaultGetInputLibelle

        if (!id) return null

        return defaultGetInputLibelleFromId({ id, items, getInputLibelle })
      }
    },

    getId: {
      type: Function,
      default: (item) => item?.id
    },

    identifier: {
      type: String,
      default: 'smm'
    },

    maxResultsHeight: {
      type: String,
      default: '20vh'
    },

    modelValue: [Object, null],
    placeholder: {
      type: String,
      default: 'Recherchez...'
    },

    loading: Boolean,
    loadingInput: Boolean,

    zIndex: {
      type: Number,
      default: 1045
    },

    allowEmpty: Boolean,
    selectOnBlur: Boolean,
    label: String,
    floatingLabel: Boolean,
    emptyValue: {
      type: [Object, String, null],
      default: () => null
    },

    disabled: Boolean,
    multiple: {
      type: Boolean,
      default: false
    },

    hideChoices: {
      type: Boolean,
      default: false
    },

    filteringFunction: {
      type: Function,
      default: ({ search, items }) => {
        return defaultFilteringFunction({ search, items })
      }
    }
  },

  emits: ['update:modelValue', 'search'],
  data() {
    return {
      showList: false,
      keepOpen: false,
      search: '',
      previousSearch: '',
      selectedId: null,
      dropdownStyle: {},
      prevRect: null, // Stocke la position précédente de l'élément
      selectedIds: this.multiple ? [] : null,
      hasJustSelected: false
    }
  },

  computed: {
    listItems() {
      const temp =
        this.multiple && this.selectedIds?.length > 0
          ? this.filteredItems.filter((item) => {
              return !this.selectedIds.includes(item.id)
            })
          : this.filteredItems

      return !this.search && this.allowEmpty ? [this.emptyValue, ...temp] : temp
    },

    filteredItems() {
      return this.filteringFunction({ search: this.search, items: this.items })
    },

    inputId() {
      return `input-autocomplete-${this.identifier}`
    },

    selectedItems() {
      if (this.multiple) {
        return this.items.filter((item) => this.selectedIds.includes(this.getId(item)))
      }
      return this.items?.find((item) => this.getId(item) === this.selectedId)
    },

    selectedItem() {
      if (this.multiple) return null
      return this.items?.find((item) => this.getId(item) === this.selectedId)
    }
  },

  watch: {
    filteredItems() {
      // Sélectionner automatiquement le premier item de la liste pour la navigation au clavier
      // sauf si on vient juste de réinitialiser (selectedId === null et pas de modelValue)
      if (this.showList && this.listItems.length > 0) {
        this.selectedId = this.getId(this.listItems[0]) ?? null
      }
    },

    search(newVal) {
      this.$emit('search', newVal)
      // Si la recherche est vidée et qu'on n'est pas en mode multiple
      if (!newVal && !this.multiple && this.selectedId !== null) {
        this.$emit('update:modelValue', null)
        this.selectedId = null
      }
    },

    modelValue: {
      handler() {
        this.handleModelValue()
      },

      deep: true,
      immediate: true
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleKey)
    this.handleModelValue()

    this.checkPosition()
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKey)
  },

  methods: {
    hexToRgb(hex) {
      if (!hex) return '255, 255, 255'
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '255, 255, 255'
    },
    checkPosition() {
      const update = () => {
        const input = this.$refs.autocompleteWrapper
        if (input) {
          const rect = input.getBoundingClientRect()
          // Si la position a changé, mettre à jour la position du dropdown
          if (
            !this.prevRect ||
            rect.top !== this.prevRect.top ||
            rect.left !== this.prevRect.left
          ) {
            this.updateDropdownPosition()
            this.prevRect = rect // Mettre à jour la position précédente
          }
        }
        requestAnimationFrame(update) // Appel à nouveau pour vérifier la position
      }
      requestAnimationFrame(update) // Lancer la première vérification
    },

    onFocus() {
      this.showList = true
      this.previousSearch = this.search
      this.updateDropdownPosition()
    },

    handleModelValue() {
      if (this.multiple) {
        this.selectedIds = Array.isArray(this.modelValue)
          ? this.modelValue.map((item) => this.getId(item))
          : []
      } else {
        if (this.modelValue && this.getId(this.modelValue) !== null) {
          this.selectedId = this.getId(this.modelValue)
          this.search = this.getInputLibelle(this.modelValue)
        } else {
          // Pas de sélection - afficher l'état par défaut
          this.search = ''
          this.selectedId = null
        }
      }
    },

    blurred() {
      this.showList = false
      if (!this.keepOpen) {
        if (this.hasJustSelected) {
          this.hasJustSelected = false
        } else if (this.listItems?.length && this.selectOnBlur) {
          this.selectItem(this.selectedId)
        } else if (!this.search && !this.multiple) {
          // Si la saisie est vide et qu'on n'est pas en mode multiple, émettre null
          this.$emit('update:modelValue', null)
          this.selectedId = null
          this.previousSearch = ''
        } else {
          this.search = this.previousSearch
        }
        this.previousSearch = ''
      }
    },

    handleKey(e) {
      if (!this.showList) return
      const index = this.listItems.findIndex((item) => this.getId(item) === this.selectedId)
      if (e.key === 'ArrowDown') {
        this.selectedId = this.getId(this.listItems[(index + 1) % this.listItems.length])
      } else if (e.key === 'ArrowUp') {
        const newIndex = index <= 0 ? this.listItems.length - 1 : index - 1
        this.selectedId = this.getId(this.listItems[newIndex])
      } else if (e.key === 'Enter') {
        this.hasJustSelected = true
        this.selectItem(this.selectedId)
        this.showList = false
        this.keepOpen = false
        this.$nextTick(() => document.getElementById(this.inputId)?.blur())
      } else if (e.key === 'Escape') {
        document.getElementById(this.inputId)?.blur()
      } else if (e.key === 'Tab') {
        this.hasJustSelected = true
        this.selectItem(this.selectedId)
        this.showList = false
        this.keepOpen = false
      }
    },

    selectItem(id) {
      const item = this.listItems.find((item) => this.getId(item) === id)

      if (this.multiple) {
        const exists = this.selectedIds.includes(id)

        if (!exists) {
          this.selectedIds.push(id)
        }

        this.$emit('update:modelValue', this.selectedItems)
        this.search = ''
        this.selectedId = null
        this.showList = false
        this.keepOpen = false
      } else {
        this.selectedId = id
        this.search = this.getInputLibelleHandler(item)
        this.$emit('update:modelValue', this.getId(item) === null ? this.emptyValue : item)
        this.showList = false
        this.keepOpen = false
      }

      this.hasJustSelected = true
    },

    deleteItem(id) {
      const index = this.selectedIds.indexOf(id)
      if (index !== -1) {
        this.selectedIds.splice(index, 1)
        this.$emit('update:modelValue', this.selectedItems)
      }
    },

    getInputLibelleHandler(item) {
      if (this.getId(item) === null) return null
      return this.getInputLibelle(item)
    },

    updateDropdownPosition() {
      this.$nextTick(() => {
        const input = this.$refs.autocompleteWrapper

        if (input) {
          const rect = input.getBoundingClientRect()
          const top = rect.bottom + window.scrollY

          this.dropdownStyle = {
            top: `${top}px`,
            left: `${rect.left + window.scrollX}px`,
            width: `${rect.width}px`,
            zIndex: this.zIndex
          }
        }
      })
    }
  }
}
</script>

<style scoped>
::placeholder {
  color: var(--c-white);
  opacity: 0.8;
  font-style: italic;
}

input {
  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.5);
  }
}

.option:hover {
  background-color: #c1c1c1 !important;
}

.autocomplete-dropdown {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* FORM FLOATING */
.form-floating {
  --input-height: 2.25rem;
  /* Modifie cette valeur et tout s'adapte */

  /* Calculs dynamiques basés sur --input-height */
  --label-default-top: 50%;
  --label-focus-top: calc(var(--input-height) * 0.25);
  --label-left: calc(var(--input-height) * 0.3);
  --label-focus-left: calc(var(--input-height) * 0.2);

  --label-font-size: calc(var(--input-height) * 0.375);
  --label-focus-size: calc(var(--input-height) * 0.25);

  --input-font-size: calc(var(--input-height) * 0.35);
  --input-padding-x: calc(var(--input-height) * 0.35);
  --input-padding-top: var(--label-focus-top);
  --input-padding-bottom: 0;

  --transition-speed: 0.2s;

  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--input-height);
}

.form-floating .floating-input {
  width: 100%;
  height: 100%;
  font-size: var(--input-font-size);
  padding: var(--input-padding-top) var(--input-padding-x) var(--input-padding-bottom)
    var(--input-padding-x);
  transition: border-color var(--transition-speed) ease-in-out;
  line-height: calc(var(--input-font-size) * 1.5);
}

/* hide placeholder */
.form-floating .floating-input::placeholder {
  color: transparent;
}

.form-floating label {
  position: absolute;
  left: var(--label-left);
  top: var(--label-default-top);
  transform: translateY(-50%);
  font-size: var(--label-font-size);
  line-height: var(--label-font-size);
  color: #6c757d;
  padding: 0 0.25rem;
  transition: all var(--transition-speed) ease-in-out;
  pointer-events: none;
}

.form-floating .floating-input:focus ~ label,
.form-floating .floating-input:not(:placeholder-shown) ~ label {
  top: var(--label-focus-top);
  left: var(--label-focus-left);
  font-size: var(--label-focus-size);
  line-height: var(--label-focus-size);
}

/* Si aucune option 'disabled' et 'hidden' n'est sélectionnée, on applique les styles par défaut */
.form-floating select.floating-input:has(option:disabled:checked[hidden]) ~ label {
  top: var(--label-default-top);
  left: var(--label-left);
  font-size: var(--label-font-size);
  line-height: var(--label-font-size);
}

/* Glass styles */
.glass-label {
  font-weight: 500;
  margin-bottom: 8px;
  font-size: var(--font-size-sm); /* 16px - was 14px */
  color: #ffffff !important;
}

/* Glass dropdown */
.autocomplete-dropdown {
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 12px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 4px 16px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.autocomplete-dropdown .option {
  background: rgba(255, 255, 255, 0.03) !important;
  border: none !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  position: relative;
}

.autocomplete-dropdown .option:last-child {
  border-bottom: none !important;
}

.autocomplete-dropdown .option::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.autocomplete-dropdown .option:hover::before {
  opacity: 1;
}

.autocomplete-dropdown .option:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

.autocomplete-dropdown .option.option-selected {
  background: rgba(107, 180, 248, 0.15) !important;
  border-left: 3px solid rgba(107, 180, 248, 0.8) !important;
  padding-left: 14px;
}

/* Option icon box - glassmorphique style */
.option-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  /* Couleurs dynamiques - Plus visibles */
  background-color: rgba(var(--option-color), 1);
  border: 1px solid rgba(var(--option-color), 0.8);
  box-shadow: 0 2px 4px rgba(var(--option-color), 1);
  transition: all 0.2s ease;
}

.option-icon-box::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
  border-radius: 10px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.option:hover .option-icon-box::before {
  opacity: 1;
}

.option:hover .option-icon-box {
  box-shadow: 0 6px 16px rgba(var(--option-color), 0.4);
}

.option-icon-box svg {
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

/* Input wrapper pour l'icône */
.input-with-icon-wrapper {
  position: relative;
  width: 100%;
}

/* Icône de catégorie sélectionnée dans l'input */
.selected-category-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  background-color: rgba(var(--selected-color), 0.8);
  border: 1px solid rgba(var(--selected-color), 1);
  box-shadow: 0 2px 8px rgba(var(--selected-color), 0.3);
  transition: all 0.2s ease;
}

/* Icône par défaut quand aucune catégorie n'est sélectionnée */
.selected-category-icon.default-icon {
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.selected-category-icon::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 0%, transparent 50%);
  border-radius: 8px;
  z-index: -1;
}

.selected-category-icon svg {
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
}

/* Icône par défaut avec opacité réduite */
.selected-category-icon.default-icon svg {
  color: rgba(255, 255, 255, 0.5);
}

/* Input avec icône - ajouter du padding à gauche */
.glass-input.input-with-icon {
  padding-left: 48px !important;
}
</style>
